import React from "react";

import { getSafe } from "@jslib/shared/utils/getSafe";
import { logger } from "@jslib/shared/utils/logger";

import {
  type AllStatuses,
  type CollectFieldsResponse,
  type FieldDescription,
  type VerificationStep,
  type ViewModel,
  type CollectFieldsAction,
} from "../types/types";
import { useAppDispatch } from "../state/contextHooks";

import { getFieldsToCollect } from "./VerificationApiUtils";

const sameFieldList = (a: FieldDescription[], b: FieldDescription[]): boolean =>
  a.length === b.length && a.every((field, i) => field.key === b[i].key);

export const useCollectFieldsEndpoint = <VM extends ViewModel = ViewModel>({
  verificationId,
  currentStep,
  viewModel,
  defaultRequiredFields = [],
}: {
  verificationId: string;
  currentStep: VerificationStep;
  viewModel: VM;
  defaultRequiredFields?: FieldDescription[];
}) => {
  const dispatch = useAppDispatch();

  // The selected status drives both the re-fetch dependency and the
  // invalidation check below. Some flows store it as a singular `status`,
  // others as a `statuses` array, so normalize to whichever is present.
  const viewModelStatuses = (viewModel as { statuses?: AllStatuses[] }).statuses;
  const viewModelStatus = (viewModel as { status?: AllStatuses }).status;
  const selectedStatus =
    Array.isArray(viewModelStatuses) && viewModelStatuses.length > 0
      ? viewModelStatuses[0]
      : viewModelStatus;

  const [requiredFields, setRequiredFields] =
    React.useState<FieldDescription[]>(defaultRequiredFields);
  const [optionalFields, setOptionalFields] = React.useState<FieldDescription[]>([]);
  const [availableStatuses, setAvailableStatuses] = React.useState<AllStatuses[] | undefined>(
    undefined,
  );

  const isFieldRequired = (key: keyof VM) => requiredFields.some((field) => field.key === key);
  const isFieldOptional = (key: keyof VM) => optionalFields.some((field) => field.key === key);

  React.useEffect(() => {
    const abortController = new AbortController();

    (async () => {
      try {
        const collectFieldsResponse: CollectFieldsResponse = await getFieldsToCollect(
          verificationId,
          currentStep,
          viewModel,
        );
        const newRequiredFields = getSafe(() => collectFieldsResponse.fieldsToCollect.required, []);
        const newOptionalFields = getSafe(() => collectFieldsResponse.fieldsToCollect.optional, []);
        if (!abortController.signal.aborted) {
          // Preserve reference identity when content hasn't changed to avoid
          // unnecessary re-renders in consuming components.
          setRequiredFields((prev) =>
            sameFieldList(prev, newRequiredFields) ? prev : newRequiredFields,
          );
          setOptionalFields((prev) =>
            sameFieldList(prev, newOptionalFields) ? prev : newOptionalFields,
          );
          setAvailableStatuses((prev) => {
            const next = collectFieldsResponse.availableStatuses;
            if (
              prev &&
              next &&
              prev.length === next.length &&
              prev.every((s, i) => s === next[i])
            ) {
              return prev;
            }
            return next;
          });

          // Dispatch the field requirements to the reducer
          const collectFieldsAction: CollectFieldsAction = {
            type: "COLLECT_FIELDS",
            requiredFields: newRequiredFields,
            optionalFields: newOptionalFields,
          };
          dispatch(collectFieldsAction);

          // If the user's currently-selected status is no longer in the
          // available list, clear it from the viewModel. Guarded by length>0
          // because empty availableStatuses is owned by the existing
          // fieldsToSkipValidation pathway in updateMilitaryViewModel et al.
          const nextStatuses = collectFieldsResponse.availableStatuses;
          if (
            selectedStatus &&
            nextStatuses &&
            nextStatuses.length > 0 &&
            !nextStatuses.includes(selectedStatus)
          ) {
            dispatch({ type: "STATUS_INVALIDATED" });
          }
        }
      } catch (error) {
        logger.error(`Failed to determine fields to collect: ${error}`, currentStep);
      }
    })();

    return () => {
      abortController.abort();
    };
  }, [verificationId, currentStep, viewModel.countryChoice?.value, selectedStatus]);

  return { isFieldRequired, isFieldOptional, availableStatuses };
};
